import * as Y from 'yjs';
import type { ConnectorItem, Item } from './schema';
import { rectsIntersect, rotatedBounds, type Rect } from './geometry';
import { SpatialIndex } from './SpatialIndex';
import { attachBoxOf, connectorBounds, connectorGeometry, readConnectorProps, type BoxLookup, type ConnectorGeometry } from './connector';

/**
 * Read-side cache over the Yjs `items` map: plain Item objects, a z-sorted list,
 * a spatial index, and a subscription API for useSyncExternalStore.
 * Item objects are replaced (never mutated) when they change, so reference equality works.
 *
 * Connectors are resolved here: their box and their ends' world points are worked out from the
 * items they are attached to (doc/connector.ts), and whenever one of those items changes the
 * connector is resolved again, so everything that reads the index sees it where it is drawn.
 */
export class ItemIndex {
  private cache = new Map<string, Item>();
  private groups = new Map<string, Set<string>>();
  private layerRank = new Map<string, number>();
  private sorted: Item[] | null = null;
  private version = 0;
  private listeners = new Set<() => void>();
  readonly spatial = new SpatialIndex();
  /** Item id -> the connectors attached to it. */
  private attached = new Map<string, Set<string>>();
  /** Connector id -> the items it is attached to. */
  private attachedTo = new Map<string, string[]>();
  /** Resolved path of each cached connector object. */
  private geometry = new WeakMap<Item, ConnectorGeometry>();
  private readonly boxOf: BoxLookup = (id) => attachBoxOf(this.cache.get(id));

  constructor(private readonly items: Y.Map<Y.Map<unknown>>) {
    // connectors last: they resolve against the items they are attached to
    const connectors: string[] = [];
    items.forEach((y, id) => {
      if (y.get('type') === 'connector') connectors.push(id);
      else this.load(id);
    });
    for (const id of connectors) this.load(id);
    items.observeDeep(this.onEvents);
  }

  private onEvents = (events: Y.YEvent<Y.AbstractType<unknown>>[]): void => {
    const touched = new Set<string>();
    for (const ev of events) {
      if (ev.target === this.items) {
        for (const key of ev.keys.keys()) touched.add(key);
      } else {
        const id = ev.path[0];
        if (typeof id === 'string') touched.add(id);
      }
    }
    // other items first, then connectors: the touched ones and those attached to anything touched
    const connectors = new Set<string>();
    for (const id of touched) {
      if (this.isConnector(id)) connectors.add(id);
      else this.load(id);
      for (const c of this.attached.get(id) ?? []) connectors.add(c);
    }
    for (const id of connectors) this.load(id);
    if (touched.size) this.bump();
  };

  private isConnector(id: string): boolean {
    return (this.items.get(id)?.get('type') ?? this.cache.get(id)?.type) === 'connector';
  }

  private load(id: string): void {
    const prev = this.cache.get(id);
    if (prev?.groupId) this.groups.get(prev.groupId)?.delete(id);
    this.unlink(id);
    const y = this.items.get(id);
    if (!y) {
      this.cache.delete(id);
      this.spatial.remove(id);
      return;
    }
    let item = { ...(y.toJSON() as Omit<Item, 'id'>), id } as Item;
    if (item.type === 'connector') item = this.resolve(item);
    this.cache.set(id, item);
    this.spatial.set(id, rotatedBounds(item));
    if (item.groupId) {
      let g = this.groups.get(item.groupId);
      if (!g) {
        g = new Set();
        this.groups.set(item.groupId, g);
      }
      g.add(id);
    }
  }

  /** A connector as stored, with its box and its ends' points worked out from its items. */
  private resolve(raw: ConnectorItem): ConnectorItem {
    const props = readConnectorProps(raw.props);
    const geo = connectorGeometry(props, this.boxOf);
    const b = connectorBounds(props, geo);
    const item: ConnectorItem = {
      ...raw,
      x: b.x,
      y: b.y,
      w: b.w,
      h: b.h,
      rotation: 0,
      props: { ...props, start: { ...props.start, x: geo.start.x, y: geo.start.y }, end: { ...props.end, x: geo.end.x, y: geo.end.y } },
    };
    this.geometry.set(item, geo);
    const targets = [props.start.item, props.end.item].filter((t): t is string => !!t);
    if (targets.length) {
      this.attachedTo.set(raw.id, targets);
      for (const t of targets) {
        let set = this.attached.get(t);
        if (!set) {
          set = new Set();
          this.attached.set(t, set);
        }
        set.add(raw.id);
      }
    }
    return item;
  }

  private unlink(id: string): void {
    const targets = this.attachedTo.get(id);
    if (!targets) return;
    this.attachedTo.delete(id);
    for (const t of targets) {
      const set = this.attached.get(t);
      set?.delete(id);
      if (set && !set.size) this.attached.delete(t);
    }
  }

  /** The connectors attached to an item. */
  connectorsOn(id: string): ReadonlySet<string> {
    return this.attached.get(id) ?? EMPTY;
  }

  /** The resolved path of a connector as the index has it. */
  connectorGeometry(item: ConnectorItem): ConnectorGeometry {
    let geo = this.geometry.get(item);
    if (!geo) {
      geo = connectorGeometry(item.props, this.boxOf);
      this.geometry.set(item, geo);
    }
    return geo;
  }

  /** Stacking rank per layer id (unknown/default = 0). Re-sorts and notifies. */
  setLayerRanks(ranks: Map<string, number>): void {
    this.layerRank = ranks;
    this.bump();
  }

  private rankOf(item: Item): number {
    return item.layerId ? (this.layerRank.get(item.layerId) ?? 0) : 0;
  }

  /** Layer order first, then z. */
  readonly compare = (a: Item, b: Item): number => {
    const ra = this.rankOf(a);
    const rb = this.rankOf(b);
    return ra !== rb ? ra - rb : byZ(a, b);
  };

  /** Ids of the items in a group (empty when unknown). */
  membersOf(groupId: string): ReadonlySet<string> {
    return this.groups.get(groupId) ?? EMPTY;
  }

  private bump(): void {
    this.version++;
    this.sorted = null;
    for (const l of this.listeners) l();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getVersion = (): number => this.version;

  get(id: string): Item | undefined {
    return this.cache.get(id);
  }

  has(id: string): boolean {
    return this.cache.has(id);
  }

  get size(): number {
    return this.cache.size;
  }

  /** All items sorted by z (bottom first). Cached until the next change. */
  all(): Item[] {
    if (!this.sorted) {
      this.sorted = [...this.cache.values()].sort(this.compare);
    }
    return this.sorted;
  }

  /** Items whose rotated bounds intersect the rect, sorted by z. */
  query(rect: Rect): Item[] {
    const out: Item[] = [];
    for (const id of this.spatial.query(rect)) {
      const item = this.cache.get(id);
      if (item && rectsIntersect(rotatedBounds(item), rect)) out.push(item);
    }
    return out.sort(this.compare);
  }

  destroy(): void {
    this.items.unobserveDeep(this.onEvents);
    this.listeners.clear();
  }
}

export const byZ = (a: Item, b: Item): number => (a.z < b.z ? -1 : a.z > b.z ? 1 : 0);

const EMPTY: ReadonlySet<string> = new Set();
